import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { euro } from '@immoaktas/shared';
import { api } from '../../../lib/api';
import { legalUrl } from '../../../shell/LegalLinks';
import { Button, Check, Dialog, SumLines } from '../../../ui';

/**
 * Order step before every web purchase (docs/BILLING.md §12, German consumer law):
 * - § 312j BGB: product, the new end price per year and what is charged today, the order button "Zahlungspflichtig
 *   bestellen". `preview` asks the server what Stripe charges now for a change of the running subscription (null →
 *   "anteilig für die Restlaufzeit"). `checkout`: a new subscription is bought on the Stripe page, the button only
 *   leads there ("Weiter zur Zahlung"), today's amount is the full yearly price.
 * - § 356 Abs. 4/5 BGB: the buyer must actively request the immediate start (checkbox, text versioned in
 *   packages/i18n); `onOrder` only runs with it, the server checks it again. Widerrufsbelehrung and AGB next to the button.
 * Never shown inside the mobile app (callers check `webPurchaseBlock`, Apple 3.1.1).
 */
export function OrderConfirmDialog({ open, onClose, onOrder, loading, product, yearlyCents, preview, checkout = false, note }: {
  open: boolean;
  onClose: () => void;
  onOrder: (withdrawalConsent: true) => void;
  loading?: boolean;
  product: ReactNode;
  /** New total per year after the order (shown when the server preview has none). */
  yearlyCents: number;
  /** Change of a running web subscription: what to preview. */
  preview?: { kind: 'units'; units: number } | { kind: 'ai' };
  checkout?: boolean;
  /** What happens to the term (e.g. "Dein Abojahr beginnt heute neu …"). */
  note?: ReactNode;
}) {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(false);
  useEffect(() => {
    if (!open) setConsent(false);
  }, [open]);
  const q = useQuery({ queryKey: ['billing-preview', preview], queryFn: () => api.subscription.preview(preview!), enabled: open && !!preview && !checkout, staleTime: 30_000, retry: false });
  const yearly = q.data?.yearlyCents ?? yearlyCents;
  const due = checkout ? yearlyCents : q.data?.dueNowCents ?? null;
  const dueText = checkout || due !== null ? euro(due) : q.isLoading ? t('order.dueLoading') : t('order.prorated');
  return (
    <Dialog open={open} onClose={onClose} sectioned title={t('order.title')}
      actions={<>
        <Button variant="secondary" onClick={onClose}>{t('order.back')}</Button>
        <Button loading={loading} disabled={!consent || (!!preview && !checkout && q.isLoading)} onClick={() => consent && onOrder(true)}>
          {checkout ? t('order.toPayment') : due !== null ? t('order.buyAmount', { amount: euro(due) }) : t('order.buy')}
        </Button>
      </>}>
      <div className="dialog-pad stack">
        <SumLines rows={[
          { label: t('order.product'), value: product },
          { label: t('order.yearly'), value: euro(yearly) },
          { label: t('order.dueNow'), value: dueText, total: true },
        ]} />
        <p className="small muted">{t('order.vat')}</p>
        {note && <p className="small">{note}</p>}
        {checkout && <p className="small">{t('order.checkoutNote')}</p>}
        <Check label={t('consumer.withdrawalConsent')} checked={consent} onChange={setConsent} />
        <p className="small"><Trans i18nKey="order.legal" components={{ withdrawal: <a href={legalUrl('/widerruf')} target="_blank" rel="noreferrer" />, form: <a href={legalUrl('/widerruf#formular')} target="_blank" rel="noreferrer" />, terms: <a href={legalUrl('/agb')} target="_blank" rel="noreferrer" /> }} /></p>
      </div>
    </Dialog>
  );
}
